// src/pages/LoginPage.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { app } from '../firebase';
import { CheckCircle, XCircle, Eye, EyeOff, Shield, Lock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui';


const LoginPage = ({ isForExtension = false }) => {
  const auth = getAuth(app);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authMode, setAuthMode] = useState('signIn');

  const startPopup = useCallback(() => signInWithPopup(auth, new GoogleAuthProvider()), [auth]);
  const startRedirect = useCallback(() => signInWithRedirect(auth, new GoogleAuthProvider()), [auth]);

  useEffect(() => {
    if (!isForExtension) return;

    const params = new URLSearchParams(window.location.search);
    const EXT_ID = params.get('extensionId') || '';
    const EXT_ORIGIN = `chrome-extension://${EXT_ID}`;

    const postAuthReady = () => { try { window.parent.postMessage('auth-ready', EXT_ORIGIN); } catch (err) { } };
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      postAuthReady();
    } else {
      window.addEventListener('DOMContentLoaded', postAuthReady, { once: true });
    }

    const sendGoogleTokens = (result) => {
      const cred = GoogleAuthProvider.credentialFromResult(result);
      if (cred?.idToken) {
        window.parent.postMessage({ googleIdToken: cred.idToken, accessToken: cred.accessToken }, EXT_ORIGIN);
      }
    };

    const startGoogleSignIn = async () => {
      try {
        const result = await startPopup();
        if (result?.user) sendGoogleTokens(result);
      } catch (err) {
        await startRedirect();
      }
    };

    const onMessage = (event) => {
      if (event.origin !== EXT_ORIGIN) return;
      if (event.data?.initAuth) startGoogleSignIn();
      else if (event.data?.ping === 'are-you-ready?') postAuthReady();
    };
    window.addEventListener('message', onMessage);

    getRedirectResult(auth)
      .then((result) => { if (result?.user) sendGoogleTokens(result); })
      .catch((error) => { console.error('Redirect sign-in failed:', error); });

    return () => window.removeEventListener('message', onMessage);
  }, [auth, isForExtension, startPopup, startRedirect]);

  const passwordRules = [
    { label: "At least 8 characters", regex: /.{8,}/ },
    { label: "At least one uppercase letter", regex: /[A-Z]/ },
    { label: "At least one lowercase letter", regex: /[a-z]/ },
    { label: "At least one number", regex: /[0-9]/ },
    { label: "At least one special character", regex: /[^A-Za-z0-9]/ }
  ];
  const validatePassword = () => {
    for (const rule of passwordRules) {
      if (!rule.regex.test(password)) return `Password must contain: ${rule.label}`;
    }
    return null;
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setMessage('');

    if (authMode === 'signUp') {
      if (!name) {
        setErr("Please enter your name.");
        return;
      }
      const validationError = validatePassword();
      if (validationError) {
        setErr(validationError);
        return;
      }
    }

    setLoading(true);
    try {
      let userCredential;

      if (authMode === 'signUp') {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName: name });
      } else {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      }

      if (userCredential.user) {
        const params = new URLSearchParams(window.location.search);
        const redirectTo = params.get('from') || '/tool';
        window.location.assign(redirectTo);
      }
    } catch (error) {
      let friendlyMessage = 'An error occurred. Please try again.';
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          friendlyMessage = 'Invalid email or password.';
          break;
        case 'auth/email-already-in-use':
          friendlyMessage = 'An account with this email already exists.';
          break;
        case 'auth/invalid-email':
          friendlyMessage = 'Please enter a valid email address.';
          break;
        default:
          break;
      }
      setErr(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setErr('');
    setMessage('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Password reset email sent! Please check your inbox.');
    } catch (error) {
      let friendlyMessage = 'Failed to send password reset email.';
      switch (error.code) {
        case 'auth/user-not-found':
          friendlyMessage = 'No user found with that email address.';
          break;
        case 'auth/invalid-email':
          friendlyMessage = 'Please enter a valid email address.';
          break;
        default:
          break;
      }
      setErr(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setErr('');
    setLoading(true);
    try {
      const result = await startPopup();
      if (result?.user) {
        const params = new URLSearchParams(window.location.search);
        const redirectTo = params.get('from') || '/tool';
        window.location.assign(redirectTo);
      }
    } catch (error) {
      let friendlyMessage = 'Google sign-in failed.';
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          friendlyMessage = 'Google sign-in popup closed.';
          break;
        case 'auth/cancelled-popup-request':
          friendlyMessage = 'Google sign-in popup already open.';
          break;
        default:
          break;
      }
      setErr(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  if (isForExtension) {
    return <div className="dark:text-white">Authenticating...</div>;
  }

  const inputClasses = "w-full p-3 border border-slate-300 dark:border-slate-600 rounded-xl text-base bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all";

  const renderContent = () => {
    if (authMode === 'forgotPassword') {
      return (
        <form onSubmit={handlePasswordReset}>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Reset Password</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">Enter your email to receive a reset link.</p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email Address"
            className={`${inputClasses} mb-4`}
            required
          />
          {message && <p className="text-brand-600 dark:text-brand-400 mb-4 text-sm">{message}</p>}
          {err && <p className="text-red-500 font-medium mb-4 text-sm">{err}</p>}
          <Button type="submit" fullWidth size="lg" disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
          <button
            type="button"
            onClick={() => setAuthMode('signIn')}
            className="mt-4 text-brand-700 dark:text-brand-400 hover:underline bg-transparent border-none cursor-pointer text-sm font-medium"
          >
            Back to Sign In
          </button>
        </form>
      );
    }

    return (
      <>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6">
          {authMode === 'signUp' ? 'Create an Account' : 'Welcome Back'}
        </h2>
        <form onSubmit={handleAuthSubmit}>
          {authMode === 'signUp' && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full Name"
              required
              className={`${inputClasses} mb-3`}
            />
          )}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className={`${inputClasses} mb-3`}
          />
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className={`${inputClasses} mb-3 pr-12`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-3.5 right-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-transparent border-none cursor-pointer"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {authMode === 'signIn' && (
            <div className="text-right mb-4">
              <button
                type="button"
                onClick={() => setAuthMode('forgotPassword')}
                className="text-sm text-brand-700 dark:text-brand-400 hover:underline bg-transparent border-none cursor-pointer font-medium"
              >
                Forgot password?
              </button>
            </div>
          )}
          {authMode === 'signUp' && (
            <div className="my-4 text-left text-sm space-y-1">
              {passwordRules.map(rule => (
                <div key={rule.label} className={`flex items-center ${rule.regex.test(password) ? 'text-brand-600 dark:text-brand-400' : 'text-slate-400 dark:text-slate-500'}`}>
                  {rule.regex.test(password) ? <CheckCircle size={14} className="mr-2 flex-shrink-0" /> : <XCircle size={14} className="mr-2 flex-shrink-0" />}
                  {rule.label}
                </div>
              ))}
            </div>
          )}
          <Button type="submit" fullWidth size="lg" disabled={loading}>
            {loading ? 'Processing...' : (authMode === 'signUp' ? 'Create Account' : 'Sign In')}
          </Button>
        </form>

        <div className="flex items-center text-slate-400 dark:text-slate-500 my-6">
          <div className="flex-1 border-b border-slate-200 dark:border-slate-600"></div>
          <span className="px-3 text-xs uppercase tracking-wider">or</span>
          <div className="flex-1 border-b border-slate-200 dark:border-slate-600"></div>
        </div>

        <Button variant="secondary" size="lg" fullWidth onClick={handleGoogleLogin} disabled={loading} className="gap-3">
          <GoogleIcon /><span>Continue with Google</span>
        </Button>

        <button
          type="button"
          onClick={() => setAuthMode(authMode === 'signIn' ? 'signUp' : 'signIn')}
          className="mt-6 text-brand-700 dark:text-brand-400 hover:underline bg-transparent border-none cursor-pointer text-sm font-medium"
        >
          {authMode === 'signIn' ? "Don't have an account? Sign Up" : 'Already have an account? Sign In'}
        </button>
        {err && <div className="mt-4 text-red-500 font-medium text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{err}</div>}
      </>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4 transition-colors duration-200">
      <div className="w-full max-w-4xl flex rounded-2xl shadow-card-prominent border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Left brand panel (desktop only) */}
        <div className="hidden md:flex md:w-5/12 bg-gradient-to-br from-brand-700 via-brand-800 to-emerald-900 p-10 flex-col justify-between text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-10 left-10 w-48 h-48 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-64 h-64 bg-brand-400 rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10">
            <img src="/images/logo_with_text_and_magnifying_glass.webp" alt="Gift Scout Logo" className="h-10 mb-8" />
            <h3 className="text-2xl font-bold mb-3">Smart savings,<br />simplified.</h3>
            <p className="text-brand-200 text-sm leading-relaxed">Securely manage your gift cards, find the best deals, and save more at checkout.</p>
          </div>
          <div className="relative z-10 space-y-3 text-sm">
            <div className="flex items-center text-brand-200">
              <Shield size={16} className="mr-2 flex-shrink-0" /> Bank-grade encryption
            </div>
            <div className="flex items-center text-brand-200">
              <Lock size={16} className="mr-2 flex-shrink-0" /> Only you can access your data
            </div>
            <div className="flex items-center text-brand-200">
              <Sparkles size={16} className="mr-2 flex-shrink-0" /> Free forever — no hidden fees
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="w-full md:w-7/12 bg-white dark:bg-slate-800 p-8 md:p-10 text-center">
          {/* Mobile logo */}
          <img src="/images/logo_with_text_and_magnifying_glass.webp" alt="Gift Scout Logo" className="h-16 mx-auto mb-6 md:hidden" />
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

export default LoginPage;